import React, { useEffect, useState } from "react";
import { HelpingHand, Search, Phone, Calendar } from "lucide-react";
import api, { fmtINR, fmtDate } from "@/lib/api";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700",
};

export default function AgentFinanceSupport() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/agent/finance-support").then((r) => setList(r.data));
  }, []);

  const filtered = list.filter((it) => {
    const s = q.toLowerCase();
    const matchQ = !q ||
      (it.customer_name || "").toLowerCase().includes(s) ||
      (it.customer_phone || "").includes(s) ||
      (it.purpose || "").toLowerCase().includes(s);
    const matchF = filter === "all" || it.status === filter;
    return matchQ && matchF;
  });

  return (
    <div data-testid="agent-finance-page">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">Finance Support</h1>
        <p className="text-sm text-slate-500">{list.length} requests from your customers · Read-only</p>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="agent-finance-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name / phone / purpose"
            className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-tenant-primary bg-white"
          />
        </div>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1">
          {["all", "pending", "approved", "rejected"].map((f) => (
            <button
              key={f}
              data-testid={`agent-finance-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-lg uppercase tracking-wider ${filter === f ? "bg-tenant-primary text-white" : "text-slate-500"}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 space-y-2.5">
        {filtered.map((it) => (
          <div key={it.id} data-testid={`agent-finance-row-${it.id}`} className="bg-white border border-slate-100 rounded-2xl p-4 card-shadow">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 grid place-items-center">
                  <HelpingHand className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-extrabold tracking-tight truncate">{it.customer_name || "Unknown"}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> +91 {it.customer_phone}</div>
                </div>
              </div>
              <span className={`text-[10px] uppercase font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[it.status] || "bg-slate-100"}`}>{it.status}</span>
            </div>

            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <Info label="Amount" value={fmtINR(it.amount)} />
              <Info label="Interest" value={`${it.interest_percent || 0}%`} />
              <Info label="Repayment" value={`${it.repayment_days || 0} days`} />
              <Info label="Requested" value={fmtDate(it.created_at)} icon={Calendar} />
            </div>
            {it.purpose && (
              <div className="mt-2 text-[12px] text-slate-600">
                <span className="text-[9px] uppercase font-bold text-slate-400 tracking-widest">Purpose</span>
                <div>{it.purpose}</div>
              </div>
            )}
            {it.note && (
              <div className="mt-2 text-[11px] text-slate-500 italic">Admin note: {it.note}</div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center text-slate-400 text-sm py-12">
            <HelpingHand className="w-10 h-10 mx-auto opacity-50" />
            <div className="mt-2">No finance support requests found.</div>
          </div>
        )}
      </div>
    </div>
  );
}

const Info = ({ label, value, icon: Icon }) => (
  <div className="bg-slate-50 rounded-lg p-2">
    <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400">{label}</div>
    <div className="font-bold text-slate-800 text-[12px] inline-flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3 text-slate-400" />} {value}
    </div>
  </div>
);
